import React, { useState, useRef, useMemo } from 'react';
import { Button, Modal, Table, Tag, Tooltip, Image, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SettingOutlined, FileImageOutlined, TagsOutlined } from '@ant-design/icons';
import { toast } from 'react-hot-toast';
import { IConfigOption } from '../../../types/configOption';
import { useConfigOptionList, useAllConfigOptions, useUpdateConfigOption } from '../../../hooks/configOption';
import { useCategoryList } from '../../../hooks/categorySetup';
import { DisplayConditionForm, DisplayConditionFormRef } from './displayConditionForm';
import { ConditionalPricesForm, ConditionalPricesFormRef } from './conditionalPricesForm';
import { ALL_OPTION_TYPES } from '../../../constants/optionTypes';
import { OPTION_UNIT_OPTIONS, DEFAULT_OPTION_UNIT } from '../../../constants';
import { useProductList } from '../../../hooks/productSetup';

interface GroupHeaderRow {
  isGroupHeader: true;
  optionType: number;
  _key: string;
}

type TableRow = IConfigOption | GroupHeaderRow;

const isGroupHeader = (row: TableRow): row is GroupHeaderRow =>
  'isGroupHeader' in row && (row as GroupHeaderRow).isGroupHeader === true;

interface CategoryOptionsTableProps {
  category: any;
  onAddOption: (categoryId: string) => void;
  onEditOption: (option: IConfigOption) => void;
  onDeleteOption: (optionId: string) => void;
}

export const CategoryOptionsTable: React.FC<CategoryOptionsTableProps> = ({
  category,
  onAddOption,
  onEditOption,
  onDeleteOption,
}) => {
  const { data: options, isLoading } = useConfigOptionList(category.id);
  const { data: productsData } = useProductList({ queryParams: { current: 1, pageSize: 100 } });
  const { data: allOptionsData } = useAllConfigOptions();
  const { data: categoriesData } = useCategoryList();
  const updateOption = useUpdateConfigOption();

  // Display conditions modal
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<IConfigOption | null>(null);
  const conditionFormRef = useRef<DisplayConditionFormRef>(null);

  // Conditional prices modal
  const [showPricesModal, setShowPricesModal] = useState(false);
  const [selectedPricesOption, setSelectedPricesOption] = useState<IConfigOption | null>(null);
  const pricesFormRef = useRef<ConditionalPricesFormRef>(null);

  const products = productsData?.value || [];

  // Group options by optionType, inserting a header row before each group
  const groupedDataSource = useMemo((): TableRow[] => {
    if (!options || options.length === 0) return [];
    const grouped = new Map<number, IConfigOption[]>();
    for (const opt of options) {
      const type = opt.dogma_optiontype ?? 0;
      if (!grouped.has(type)) grouped.set(type, []);
      grouped.get(type)!.push(opt);
    }
    const result: TableRow[] = [];
    const sortedTypes = [...grouped.keys()].sort((a, b) => a - b);
    for (const type of sortedTypes) {
      result.push({ isGroupHeader: true, optionType: type, _key: `group-${type}` });
      result.push(...grouped.get(type)!);
    }
    return result;
  }, [options]);

  const TOTAL_COLUMNS = 9;

  const handleOpenConditions = (option: IConfigOption) => {
    setSelectedOption(option);
    setShowConditionModal(true);
  };

  const handleCloseConditions = () => {
    setShowConditionModal(false);
    setSelectedOption(null);
  };

  const handleSaveConditions = async () => {
    if (!selectedOption || !conditionFormRef.current) return;

    try {
      const condition = conditionFormRef.current.getCondition();
      await updateOption.mutateAsync({
        id: selectedOption.dogma_configurationoptionsid,
        payload: {
          dogma_displaycondition: JSON.stringify(condition),
        },
        categoryId: category.id,
      });
      toast.success('Display conditions saved successfully');
      handleCloseConditions();
    } catch (error) {
      toast.error('Failed to save display conditions');
    }
  };

  const handleOpenPrices = (option: IConfigOption) => {
    setSelectedPricesOption(option);
    setShowPricesModal(true);
  };

  const handleClosePrices = () => {
    setShowPricesModal(false);
    setSelectedPricesOption(null);
  };

  const handleSavePrices = async () => {
    if (!selectedPricesOption || !pricesFormRef.current) return;
    const prices = pricesFormRef.current.getPrices();
    try {
      await updateOption.mutateAsync({
        id: selectedPricesOption.dogma_configurationoptionsid,
        payload: { dogma_pricelist: JSON.stringify(prices) },
        categoryId: category.id,
      });
      toast.success('Price rules saved successfully');
      handleClosePrices();
    } catch (error) {
      toast.error('Failed to save price rules');
    }
  };

  const columns = [
    {
      title: 'Option',
      dataIndex: 'dogma_name',
      key: 'dogma_name',
      render: (text: string, record: TableRow) => {
        if (isGroupHeader(record)) {
          const optionType = ALL_OPTION_TYPES.find((t) => t.value === record.optionType);
          return {
            children: (
              <div className="flex items-center gap-2 py-0.5">
                <Tag color="geekblue" className="font-semibold text-xs m-0">
                  {optionType?.label || `Type ${record.optionType}`}
                </Tag>
              </div>
            ),
            props: { colSpan: TOTAL_COLUMNS },
          };
        }
        const opt = record as IConfigOption;
        return (
          <div>
            <div className="font-medium">{opt.dogma_name}</div>
            {opt.dogma_description && (
              <div className="text-xs text-text-secondary">{opt.dogma_description}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Image',
      dataIndex: 'dogma_picturebloburl',
      key: 'dogma_picturebloburl',
      align: 'center' as const,
      width: 80,
      render: (imageUrl: string, record: TableRow) => {
        if (isGroupHeader(record)) return { props: { colSpan: 0 } };
        if (imageUrl) {
          return (
            <Image
              src={imageUrl}
              alt="Option"
              width={50}
              height={50}
              style={{ objectFit: 'cover', borderRadius: '4px' }}
              preview={{ mask: 'View' }}
            />
          );
        }
        return <FileImageOutlined className="text-gray-300 text-2xl" />;
      },
    },
    {
      title: 'Price Modifier',
      dataIndex: 'dogma_pricemodifier',
      key: 'dogma_pricemodifier',
      render: (value: number = 0, record: TableRow) => {
        if (isGroupHeader(record)) return { props: { colSpan: 0 } };
        return (
          <span className={`font-semibold ${value > 0 ? 'text-red-500' : value < 0 ? 'text-green-600' : 'text-gray-500'}`}>
            {value}
          </span>
        );
      },
    },
    {
      title: 'Unit',
      dataIndex: 'dogma_optionunit',
      key: 'dogma_optionunit',
      render: (unit: number | undefined, record: TableRow) => {
        if (isGroupHeader(record)) return { props: { colSpan: 0 } };
        const resolved = unit ?? DEFAULT_OPTION_UNIT;
        const match = OPTION_UNIT_OPTIONS.find((u) => u.value === resolved);
        return <Tag color="purple">{match?.label ?? 'Per Window'}</Tag>;
      },
    },
    {
      title: 'Type',
      dataIndex: 'dogma_optiontype',
      key: 'dogma_optiontype',
      render: (type: number, record: TableRow) => {
        if (isGroupHeader(record)) return { props: { colSpan: 0 } };
        const optionType = ALL_OPTION_TYPES.find((opt) => opt.value === type);
        return <Tag>{optionType?.label || 'N/A'}</Tag>;
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: TableRow) => {
        if (isGroupHeader(record)) return { props: { colSpan: 0 } };
        return <Tag color="success">Active</Tag>;
      },
    },
    {
      title: 'Valid For Products',
      key: 'validProducts',
      width: 250,
      render: (_: any, record: TableRow) => {
        if (isGroupHeader(record)) return { props: { colSpan: 0 } };
        const opt = record as IConfigOption;
        let validProductIds: string[] = [];
        try {
          if (opt.dogma_displaycondition) {
            const displayCondition = JSON.parse(opt.dogma_displaycondition);
            validProductIds = displayCondition?.validForProducts || [];
          }
        } catch (e) {
          validProductIds = [];
        }

        if (validProductIds.length === 0) {
          return <Tag color="default">All Products</Tag>;
        }

        const validProducts = products.filter((p) => validProductIds.includes(p.productid));
        if (validProducts.length === 0) {
          return <Tag color="default">No Products</Tag>;
        }

        const displayProducts = validProducts.slice(0, 2);
        const remainingCount = validProducts.length - 2;

        return (
          <div className="flex flex-wrap gap-1">
            {displayProducts.map((product) => (
              <Tag key={product.productid} color="blue" className="text-xs">
                {product.name}
              </Tag>
            ))}
            {remainingCount > 0 && (
              <Tooltip
                title={
                  <div>
                    {validProducts.slice(2).map((p) => (
                      <div key={p.productid}>{p.name}</div>
                    ))}
                  </div>
                }
              >
                <Tag color="blue" className="text-xs cursor-pointer">
                  +{remainingCount} more
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Conditions',
      key: 'conditions',
      align: 'center' as const,
      render: (_: any, record: TableRow) => {
        if (isGroupHeader(record)) return { props: { colSpan: 0 } };
        const opt = record as IConfigOption;
        let hasCondition = false;
        try {
          if (opt.dogma_displaycondition) {
            const condition = JSON.parse(opt.dogma_displaycondition);
            hasCondition =
              (condition.dependsOn && condition.dependsOn.length > 0) ||
              (condition.validForProducts && condition.validForProducts.length > 0);
          }
        } catch (e) {
          hasCondition = false;
        }
        return (
          <Button
            type={hasCondition ? 'primary' : 'text'}
            size="small"
            icon={<SettingOutlined />}
            onClick={() => handleOpenConditions(opt)}
          >
            Setup
          </Button>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center' as const,
      render: (_: any, record: TableRow) => {
        if (isGroupHeader(record)) return { props: { colSpan: 0 } };
        const opt = record as IConfigOption;
        return (
          <div className="flex gap-1 justify-center">
            <Tooltip title="Price List">
              <Button
                type={(() => {
                  try {
                    const val = opt.dogma_pricelist;
                    if (val) {
                      const parsed = JSON.parse(val);
                      return Array.isArray(parsed) && parsed.length > 0 ? 'primary' : 'text';
                    }
                  } catch { /* not set */ }
                  return 'text';
                })()}
                size="small"
                icon={<TagsOutlined />}
                onClick={() => handleOpenPrices(opt)}
              />
            </Tooltip>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEditOption(opt)}
            />
            <Popconfirm
              title="Delete Option"
              description={`Are you sure you want to delete "${opt.dogma_name}"?`}
              onConfirm={() => onDeleteOption(opt.dogma_configurationoptionsid)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                danger
              />
            </Popconfirm>
          </div>
        );
      },
    },
  ];
  return (
    <div className="mb-6 border border-neutral-400 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-base font-semibold text-text-primary">{category.name}</h3>
          {category.description && (
            <p className="text-sm text-text-secondary">{category.description}</p>
          )}
        </div>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => onAddOption(category.id)}
        >
          Add Option
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={groupedDataSource}
        loading={isLoading}
        rowKey={(record: TableRow) =>
          isGroupHeader(record)
            ? record._key
            : (record as IConfigOption).dogma_configurationoptionsid
        }
        size="small"
        pagination={false}
        onRow={(record: TableRow) => ({
          style: isGroupHeader(record)
            ? { backgroundColor: '#f0f5ff', cursor: 'default' }
            : {},
        })}
      />

      <Modal
        title={`Display Conditions: ${selectedOption?.dogma_name || ''}`}
        open={showConditionModal}
        onCancel={handleCloseConditions}
        onOk={handleSaveConditions}
        width={800}
        okText="Save Conditions"
        cancelText="Close"
        confirmLoading={updateOption.isPending}
      >
        <DisplayConditionForm
          key={selectedOption?.dogma_configurationoptionsid}
          ref={conditionFormRef}
          initialCondition={(() => {
            try {
              return selectedOption?.dogma_displaycondition
                ? JSON.parse(selectedOption.dogma_displaycondition)
                : undefined;
            } catch (e) {
              return undefined;
            }
          })()}
        />
      </Modal>

      <Modal
        title={`Price List: ${selectedPricesOption?.dogma_name || ''}`}
        open={showPricesModal}
        onCancel={handleClosePrices}
        onOk={handleSavePrices}
        width={800}
        okText="Save Price Rules"
        cancelText="Close"
        confirmLoading={updateOption.isPending}
      >
        <ConditionalPricesForm
          key={selectedPricesOption?.dogma_configurationoptionsid}
          ref={pricesFormRef}
          allOptions={allOptionsData ?? []}
          categories={categoriesData?.value ?? []}
          initialPrices={(() => {
            try {
              const val = selectedPricesOption?.dogma_pricelist;
              if (val) {
                const parsed = JSON.parse(val);
                if (Array.isArray(parsed)) return parsed;
              }
            } catch {
              // not yet set
            }
            return [];
          })()}
        />
      </Modal>
    </div>
  );
};
